import type { DeepPartial, MessageWidgetStyles, TimelineEntity } from '@platform/timeline';
import { filterByMode, Timeline } from '@platform/timeline';
import { Box, Divider, Icon, IconButton, TextField, Typography } from '@shared/ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const sanitizeSegment = (val: string) =>
  val
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'default';

const DRAG_HANDLE_WIDTH = 12;
const COLLAPSED_WIDTH = 10;
const COLLAPSED_HOVER_WIDTH = 30;
const COLLAPSED_HIT_AREA = 32;

export interface SidebarChatProps {
  /** Title of the sidebar chat */
  title?: string;
  /** Subtitle or helper text */
  subtitle?: string;
  /** Timeline entities to display */
  entities: TimelineEntity[];
  /** Conversation ID for widget-level context */
  convId?: string;
  /** Placeholder text for the input field */
  placeholder?: string;
  /** Callback when a message is sent */
  onSendMessage?: (message: string) => void | Promise<void>;
  /** Whether the chat is currently loading/processing */
  isLoading?: boolean;
  /** Auto-scroll to bottom when new messages arrive */
  autoScroll?: boolean;
  /** Debug mode to show additional info */
  debugMode?: boolean;
  /** Width of the sidebar (default: 420px) */
  width?: number | string;
  /** Height of the sidebar (default: 100vh) */
  height?: number | string;
  /** Whether to show a header */
  showHeader?: boolean;
  /** Custom header content */
  headerContent?: React.ReactNode;
  /** Whether input is disabled */
  inputDisabled?: boolean;
  /** Show settings button in header */
  showSettings?: boolean;
  /** Callback when settings is clicked */
  onSettingsClick?: () => void;
  /** Show close button in header */
  showClose?: boolean;
  /** Callback when close is clicked */
  onCloseClick?: () => void;
  /** Optional storage id to isolate draft input across instances; falls back to title + path */
  storageId?: string;
  /** Minimum resize width in px (default: 280) */
  minWidth?: number;
  /** Maximum resize width in px (default: 720) */
  maxWidth?: number;
  /** Start collapsed (default: false) */
  defaultCollapsed?: boolean;
  /** Called whenever the visible width changes so the parent can adjust margins */
  onWidthChange?: (width: number) => void;
}

export function SidebarChat({
  title = 'Ask Anything',
  subtitle,
  entities,
  convId,
  placeholder = 'Ask Anything',
  onSendMessage,
  isLoading = false,
  autoScroll = true,
  debugMode = false,
  width = 420,
  height = '100vh',
  showHeader = true,
  headerContent,
  inputDisabled = false,
  showSettings = false,
  onSettingsClick,
  showClose = false,
  onCloseClick,
  storageId,
  minWidth = 280,
  maxWidth = 720,
  defaultCollapsed = false,
  onWidthChange,
}: SidebarChatProps) {
  const initialWidth = typeof width === 'number' ? width : 420;
  const [sidebarWidth, setSidebarWidth] = useState(initialWidth);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isDragging, setIsDragging] = useState(false);
  const [isHoveringCollapsed, setIsHoveringCollapsed] = useState(false);
  const [isHoveringHandle, setIsHoveringHandle] = useState(false);

  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const onWidthChangeRef = useRef(onWidthChange);

  useEffect(() => {
    onWidthChangeRef.current = onWidthChange;
  }, [onWidthChange]);

  useEffect(() => {
    if (typeof width === 'number') {
      setSidebarWidth(width);
    }
  }, [width]);

  const displayWidth = isCollapsed ? COLLAPSED_WIDTH : sidebarWidth;

  useEffect(() => {
    if (isCollapsed) return;
    onWidthChangeRef.current?.(displayWidth);
  }, [displayWidth, isCollapsed]);

  useEffect(() => {
    if (!isCollapsed) return;
    onWidthChangeRef.current?.(COLLAPSED_WIDTH);
  }, [isCollapsed]);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (typeof width !== 'number') return;
      e.preventDefault();
      dragStartX.current = e.clientX;
      dragStartWidth.current = sidebarWidth;
      setIsDragging(true);
    },
    [sidebarWidth, width]
  );

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      const delta = dragStartX.current - e.clientX;
      setSidebarWidth(Math.min(maxWidth, Math.max(minWidth, dragStartWidth.current + delta)));
    };
    const onMouseUp = () => setIsDragging(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, maxWidth, minWidth]);

  const collapse = useCallback(() => setIsCollapsed(true), []);
  const expand = useCallback(() => {
    setIsCollapsed(false);
    setIsHoveringCollapsed(false);
  }, []);

  const [pathname, setPathname] = useState(() => {
    if (typeof window === 'undefined' || !window.location?.pathname) return 'unknown';
    return window.location.pathname;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => {
      const next = window.location?.pathname || 'unknown';
      setPathname((prev) => (prev !== next ? next : prev));
    };
    update();
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  const storageKey = useMemo(() => {
    const path = pathname ? sanitizeSegment(pathname) : 'unknown';
    const id = storageId?.trim()
      ? sanitizeSegment(storageId.trim())
      : sanitizeSegment(title || 'sidebar');
    return `sidebarChat.input.${id}.${path}`;
  }, [storageId, title, pathname]);

  const [inputValue, setInputValue] = useState(() => {
    try {
      const path = pathname ? sanitizeSegment(pathname) : 'unknown';
      const id = storageId?.trim()
        ? sanitizeSegment(storageId.trim())
        : sanitizeSegment(title || 'sidebar');
      return sessionStorage.getItem(`sidebarChat.input.${id}.${path}`) || '';
    } catch {
      return '';
    }
  });
  const [isDebugMode, setIsDebugMode] = useState(debugMode || false);

  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousStorageKeyRef = useRef<string>(storageKey);
  const isTransitioningStorageKeyRef = useRef<boolean>(false);

  useEffect(() => {
    if (storageKey === previousStorageKeyRef.current) return;
    isTransitioningStorageKeyRef.current = true;
    try {
      setInputValue(sessionStorage.getItem(storageKey) ?? '');
    } catch {
      /* ignore */
    }
    previousStorageKeyRef.current = storageKey;
    setTimeout(() => {
      isTransitioningStorageKeyRef.current = false;
    }, 0);
  }, [storageKey]);

  useEffect(() => {
    if (isTransitioningStorageKeyRef.current) return;
    if (storageKey !== previousStorageKeyRef.current) return;
    try {
      if (inputValue) {
        sessionStorage.setItem(storageKey, inputValue);
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch {
      /* ignore */
    }
  }, [inputValue, storageKey]);

  const filteredEntities = isDebugMode
    ? filterByMode(entities, 'debug')
    : filterByMode(entities, 'normal');

  const messageWidgetStyles: DeepPartial<MessageWidgetStyles> = useMemo(
    () => ({
      user: {
        backgroundColor: 'rgba(255,255,255,0.12)',
        textColor: '#ffffff',
        inlineCodeBgColor: 'rgba(255,255,255,0.15)',
        inlineCodeTextColor: '#ffffff',
      },
      assistant: {
        backgroundColor: 'transparent',
        textColor: 'rgba(255,255,255,0.92)',
        inlineCodeBgColor: 'rgba(255,255,255,0.15)',
        inlineCodeTextColor: 'rgba(255,255,255,0.92)',
        streamingIndicatorColor: 'rgba(255,255,255,0.72)',
        streamingProgressColor: 'rgba(255,255,255,0.92)',
        streamingProgressTrackColor: 'rgba(255,255,255,0.18)',
      },
    }),
    []
  );

  const filteredEntityCount = filteredEntities.length;

  useEffect(() => {
    if (!autoScroll || !timelineContainerRef.current) return;
    if (filteredEntityCount === 0) {
      timelineContainerRef.current.scrollTop = 0;
      return;
    }
    timelineContainerRef.current.scrollTop = timelineContainerRef.current.scrollHeight;
  }, [autoScroll, filteredEntityCount]);

  const handleSendMessage = () => {
    if (!inputValue.trim() || inputDisabled || isLoading) return;
    const msg = inputValue;
    setInputValue('');
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    setTimeout(() => inputRef.current?.focus(), 0);
    onSendMessage?.(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: displayWidth,
        height: height || '100%',
        flexShrink: 0,
        overflow: 'visible',
        transition: isDragging ? 'none' : 'width 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      {isCollapsed ? (
        <Box
          role="button"
          aria-label="expand sidebar"
          tabIndex={0}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: COLLAPSED_HIT_AREA,
            cursor: 'pointer',
            zIndex: 10,
          }}
          onMouseEnter={() => setIsHoveringCollapsed(true)}
          onMouseLeave={() => setIsHoveringCollapsed(false)}
          onClick={expand}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              expand();
            }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: isHoveringCollapsed ? COLLAPSED_HOVER_WIDTH : COLLAPSED_WIDTH,
              bgcolor: '#637A6F',
              borderLeft: '1px solid rgba(255,255,255,0.12)',
              transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Icon
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: 14,
                opacity: isHoveringCollapsed ? 1 : 0,
                transition: 'opacity 0.15s',
              }}
            >
              chevron_left
            </Icon>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: 'absolute',
              left: -(DRAG_HANDLE_WIDTH / 2),
              top: 0,
              bottom: 0,
              width: DRAG_HANDLE_WIDTH,
              cursor: typeof width === 'number' ? 'ew-resize' : 'default',
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseDown={handleResizeMouseDown}
            onMouseEnter={() => setIsHoveringHandle(true)}
            onMouseLeave={() => setIsHoveringHandle(false)}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: 2,
                transform: 'translateX(-50%)',
                bgcolor: '#637A6F',
                opacity:
                  typeof width === 'number' ? (isDragging ? 0.7 : isHoveringHandle ? 0.35 : 0) : 0,
                transition: 'opacity 0.15s',
                pointerEvents: 'none',
              }}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 0,
              minHeight: 0,
              bgcolor: '#637A6F',
              borderLeft: '1px solid',
              borderColor: 'rgba(255,255,255,0.12)',
              position: 'relative',
            }}
          >
            {showHeader ? (
              <>
                <Box
                  sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 52,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {headerContent || (
                      <>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#ffffff', lineHeight: 1.3 }}
                        >
                          {title}
                        </Typography>
                        {subtitle && (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'rgba(255,255,255,0.6)' }}
                          >
                            {subtitle}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 0 }}>
                    <Box
                      component="button"
                      onClick={() => setIsDebugMode(!isDebugMode)}
                      sx={{
                        px: 1,
                        py: 0.25,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        border: '1px solid',
                        borderColor: isDebugMode
                          ? 'rgba(255,255,255,0.8)'
                          : 'rgba(255,255,255,0.3)',
                        borderRadius: '4px',
                        bgcolor: isDebugMode ? 'rgba(255,255,255,0.15)' : 'transparent',
                        color: isDebugMode ? '#ffffff' : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        letterSpacing: '0.02em',
                        transition: 'all 0.15s',
                        '&:hover': {
                          borderColor: 'rgba(255,255,255,0.7)',
                          color: 'rgba(255,255,255,0.85)',
                        },
                      }}
                    >
                      {isDebugMode ? 'Debug: ON' : 'Debug'}
                    </Box>
                    {showSettings && (
                      <IconButton
                        size="small"
                        onClick={onSettingsClick}
                        aria-label="settings"
                        sx={{
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                      >
                        <Icon sx={{ fontSize: '1.1rem' }}>settings</Icon>
                      </IconButton>
                    )}
                    {showClose && (
                      <IconButton
                        size="small"
                        onClick={onCloseClick}
                        aria-label="close"
                        sx={{
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                      >
                        <Icon sx={{ fontSize: '1.1rem' }}>close</Icon>
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={collapse}
                      aria-label="collapse sidebar"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      <Icon sx={{ fontSize: '1.1rem' }}>chevron_right</Icon>
                    </IconButton>
                  </Box>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
              </>
            ) : (
              <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                <IconButton
                  size="small"
                  onClick={collapse}
                  aria-label="collapse sidebar"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <Icon sx={{ fontSize: '1.1rem' }}>chevron_right</Icon>
                </IconButton>
              </Box>
            )}

            <Box
              ref={timelineContainerRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 2,
                bgcolor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box sx={{ flex: '1 1 auto' }} />
              <Timeline
                entities={filteredEntities}
                autoScroll={false}
                debugMode={isDebugMode}
                hideRoleLabels={true}
                hideTimestamps={true}
                convId={convId}
                userBgColor="rgba(255,255,255,0.12)"
                assistantBgColor="transparent"
                messageWidgetStyles={messageWidgetStyles}
              />
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 1.5,
                bgcolor: 'transparent',
              }}
            >
              <Box sx={{ borderRadius: '16px', bgcolor: 'background.paper', overflow: 'hidden' }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  disabled={inputDisabled || isLoading}
                  inputRef={inputRef}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || inputDisabled || isLoading}
                        sx={{
                          bgcolor: 'primary.main',
                          color: '#fff',
                          width: 34,
                          height: 34,
                          flexShrink: 0,
                          transition: 'background-color 0.15s',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': {
                            bgcolor: '#e0e0e0',
                            color: 'rgba(0,0,0,0.3)',
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: '1.1rem !important' }}>
                          {isLoading ? 'hourglass_empty' : 'arrow_upward'}
                        </Icon>
                      </IconButton>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      paddingLeft: '16px',
                      paddingRight: '12px',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      '& fieldset': { border: 'none' },
                    },
                    '& .MuiInputBase-input': {
                      padding: '0',
                      fontSize: '0.9375rem',
                      fontWeight: 500,
                      color: '#1a1a1a',
                      '&::placeholder': { color: 'text.disabled', opacity: 1 },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default SidebarChat;
